import { ref, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';

export interface FeatureFlag {
  t: string; // type (e.g., 'b' for boolean)
  v: boolean | string | number; // value
}

export interface FeatureFlags {
  [key: string]: FeatureFlag;
}

/** The Kinde boolean flag that shows the Tasks feature in Web Admin (page, menu, customer tab, rep link, dashboard card). Off unless the token carries it. */
export const TASKS_FEATURE_FLAG = 'tasks';

/** The Kinde boolean flag that shows "Scan business card" on Customers and Contacts. Off unless the token carries it. */
export const BUSINESS_CARD_SCAN_FEATURE_FLAG = 'business-card-scan';

/** The Kinde boolean flag for customer domain intelligence: domain research suggestions (and later the Domain field). Off unless the token carries it. */
export const CUSTOMER_DOMAIN_INTELLIGENCE_FEATURE_FLAG = 'customer-domain-intelligence';

/** Campaigns and discount codes (CU-86bcj36z8): the Campaigns pages and the cart's code control. */
export const CAMPAIGNS_FEATURE_FLAG = 'campaigns';

// Global reactive state for feature flags
const featureFlags = ref<FeatureFlags>({});
const lastParsedToken = ref<string>('');

/**
 * Parse feature flags from JWT token payload
 */
const parseTokenFeatureFlags = (token: string): FeatureFlags => {
  try {
    if (!token) return {};
    
    const base64Url = token.split('.')[1];
    if (!base64Url) return {};
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    
    return payload.feature_flags || {};
  } catch (error) {
    console.error('Error parsing feature flags from token:', error);
    return {};
  }
};

/**
 * Update feature flags from current auth token
 */
const updateFeatureFlags = async () => {
  const authStore = useAuthStore();
  try {
    const token = await authStore.getAuthorizationToken();
    
    // Only parse if token has changed to avoid unnecessary work
    if (token && token !== lastParsedToken.value) {
      featureFlags.value = parseTokenFeatureFlags(token);
      lastParsedToken.value = token;
      console.log('Feature flags updated:', featureFlags.value);
    }
  } catch (error) {
    console.error('Error updating feature flags:', error);
    featureFlags.value = {};
  }
};

/**
 * Force refresh feature flags by getting a fresh token
 */
const forceUpdateFeatureFlags = async () => {
  const authStore = useAuthStore();
  try {
    // Force refresh the token to get latest feature flags from Kinde
    await authStore.refreshAccessToken();
    const token = authStore.accessToken;
    
    if (token) {
      featureFlags.value = parseTokenFeatureFlags(token);
      lastParsedToken.value = token;
      console.log('Feature flags force updated:', featureFlags.value);
    }
  } catch (error) {
    console.error('Error force updating feature flags:', error);
    featureFlags.value = {};
  }
};

/**
 * Main composable for feature flag functionality
 */
export const useFeatureFlags = () => {
  const authStore = useAuthStore();

  // Watch for authentication changes and update flags
  watch(() => authStore.isAuthenticated, async (isAuth) => {
    if (isAuth) {
      await updateFeatureFlags();
    } else {
      featureFlags.value = {};
      lastParsedToken.value = '';
    }
  }, { immediate: true });

  // Also watch for token changes specifically (for refreshes)
  watch(() => authStore.accessToken, async (newToken) => {
    if (newToken && authStore.isAuthenticated) {
      // Only update if the token actually changed
      if (newToken !== lastParsedToken.value) {
        console.log('Token changed, updating feature flags...');
        featureFlags.value = parseTokenFeatureFlags(newToken);
        lastParsedToken.value = newToken;
        console.log('Feature flags updated from token change:', featureFlags.value);
      }
    }
  });

  /**
   * Check if a feature flag is enabled
   * @param flagName - The name of the feature flag
   * @param defaultValue - Default value if flag doesn't exist
   */
  const isFeatureEnabled = (flagName: string, defaultValue: boolean = false): boolean => {
    const flag = featureFlags.value[flagName];
    if (!flag) return defaultValue;
    
    // Handle boolean flags
    if (flag.t === 'b') {
      return Boolean(flag.v);
    }
    
    // For other types, check if value is truthy
    return Boolean(flag.v);
  };

  /**
   * Get the raw value of a feature flag
   * @param flagName - The name of the feature flag
   * @param defaultValue - Default value if flag doesn't exist
   */
  const getFeatureValue = <T = any>(flagName: string, defaultValue?: T): T => {
    const flag = featureFlags.value[flagName];
    return flag ? (flag.v as T) : (defaultValue as T);
  };

  /**
   * Get all available feature flags
   */
  const getAllFeatureFlags = computed(() => featureFlags.value);

  /**
   * Check if a feature flag exists
   * @param flagName - The name of the feature flag
   */
  const hasFeatureFlag = (flagName: string): boolean => {
    return flagName in featureFlags.value;
  };

  /**
   * Manually refresh feature flags (useful after token refresh)
   */
  const refreshFeatureFlags = async () => {
    await updateFeatureFlags();
  };

  /**
   * Force refresh feature flags by getting a fresh token from Kinde
   */
  const forceRefreshFeatureFlags = async () => {
    await forceUpdateFeatureFlags();
  };

  /**
   * Reactive computed for specific feature flags (for use in templates)
   */
  const createFeatureFlag = (flagName: string, defaultValue: boolean = false) => {
    return computed(() => isFeatureEnabled(flagName, defaultValue));
  };

  return {
    isFeatureEnabled,
    getFeatureValue,
    getAllFeatureFlags,
    hasFeatureFlag,
    refreshFeatureFlags,
    forceRefreshFeatureFlags,
    createFeatureFlag
  };
};

// Export commonly used feature flags as reactive computeds
export const useCommonFeatureFlags = () => {
  const { createFeatureFlag } = useFeatureFlags();
  
  return {
    integrationsEnabled: createFeatureFlag('integrations', false),
    vectorSearchEnabled: createFeatureFlag('vector-search', false),
    salesForecastingEnabled: createFeatureFlag('sales-forecasting', false),
    // The cross-customer Contacts page and the per-contact Edit / Remove on Customer Details
    // (CU-86bcm14m5). Environment default off; enabled per organization for pilots.
    contactsPageEnabled: createFeatureFlag('contacts-page', false),
    tasksEnabled: createFeatureFlag(TASKS_FEATURE_FLAG, false),
    businessCardScanEnabled: createFeatureFlag(BUSINESS_CARD_SCAN_FEATURE_FLAG, false),
    customerDomainIntelligenceEnabled: createFeatureFlag(CUSTOMER_DOMAIN_INTELLIGENCE_FEATURE_FLAG, false),
    // Add more common flags as needed
  };
};
